import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Table, Input, Tooltip, Tag, Spin } from "antd";
import { Collapse } from "antd";
import { getRequest, patchRequest, postRequest } from "../../axios";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";
import PropTypes from "prop-types";


const { Panel } = Collapse;

const ViewRFQDiameterWise = ({getResponseData}) => {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tableData, setTableData] = useState([]);
  const [rfqMaterialDetailsID, setRfqMaterialDetailsID] = useState(false);
  const [response, setResponse] = useState(null);
  const [diameterList, setDiameterList] = useState([]);
  const [masterId, setMasterId] = useState(null);
  const [cityLength, setCityLength] = useState(1);
  const [apiLoading, setApiLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
    
  useEffect(() => {
    const rfqGetById = async () => {
      setApiLoading(true);
      try {
        const apiResponse = getResponseData
        if (apiResponse) {
          const rfqDetailsData = apiResponse;
          setResponse(rfqDetailsData);
          setMasterId(rfqDetailsData.purchase_requisition_master_id); 
          const allDiameters = new Set();
          rfqDetailsData.city_wise_pr?.forEach((city) => {
            city.site_wise_details.forEach((site) => {
              site.material_wise_details.forEach((material) => {
                material.diameter_wise?.forEach((dia) => {
                  allDiameters.add(`${dia.diameter_name.toUpperCase()}`);
                });
              });
            });
          });
          setDiameterList(
            Array.from(allDiameters).sort((a, b) => parseFloat(a) - parseFloat(b))
          );
        } else {
          ErrorNotificationMsg(t("error_in_rfq_detail"));
        }
      } catch (error) {
        console.error("Error fetching RFQ details", error);
        ErrorNotificationMsg(t("error_in_rfq_detail"));
      } finally {
        setApiLoading(false);
      }
    };

    if (getResponseData) {
      rfqGetById();
    }
  }, [getResponseData]);

  useEffect(() => {
    if (response && response.city_wise_pr) {
      updateTableData(response.city_wise_pr);
      setRfqMaterialDetailsID(!!response.rfq_material_details?.length);
    }
  }, [response]);

  const updateTableData = (cityWisePr) => {
    if (!Array.isArray(cityWisePr)) return;

    const tableData = [];
    setCityLength(cityWisePr.length);

    cityWisePr.forEach((city, cityIndex) => {
      const cityRowSpan = city.site_wise_details.length;
      let cityTotalQuantity = 0;
      let cityTotalQuantityInKg = 0;
      let cityTotalAmount = 0;

      city.site_wise_details.forEach((site, siteIndex) => {
        let siteTotalQuantity = 0;
        let siteTotalQuantityInKg = 0;
        let siteTotalAmount = 0;
        const seenSubCategories = new Set();

        const groupedMaterials = site.material_wise_details.reduce(
          (acc, material) => {
            const {
              material_type_name,
              material_type_sub_category,
              pr_total_quantity,
              pr_total_quantity_by_kg,
              estimated_material_unit_price,
              pr_total_amount,
              purchase_requisition_material_id,
              rfq_material_id,
              preferred_unit_of_measurement,
              preferred_unit_of_measurement_id,
              material_type_uom,
              material_type_uom_name,
              remark,
              vendors,
              diameter_wise,
            } = material;

            if (!acc[material_type_name]) {
              acc[material_type_name] = {
                material_type_name,
                material_type_sub_category,
                total_quantity: 0,
                total_quantity_by_kg: 0,
                total_amount: 0,
                estimated_material_unit_price,
                purchase_requisition_material_id,
                rfq_material_id,
                preferred_unit_of_measurement,
                preferred_unit_of_measurement_id,
                material_type_uom,
                material_type_uom_name,
                remark,
                vendors,
                diameter_wise_map: {},
              };
            }

            acc[material_type_name].total_quantity += pr_total_quantity;
            acc[material_type_name].total_quantity_by_kg += pr_total_quantity_by_kg;
            acc[material_type_name].total_amount += pr_total_amount;

            (diameter_wise || []).forEach((dia) => {
              const key = dia.diameter_id;
              if (!acc[material_type_name].diameter_wise_map[key]) {
                acc[material_type_name].diameter_wise_map[key] = {
                  ...dia,
                  quantity: 0,
                };
              }
              acc[material_type_name].diameter_wise_map[key].quantity += dia.quantity;
            });

            return acc;
          },
          {}
        );

        const materials = Object.values(groupedMaterials);

        materials.forEach((material) => {
          siteTotalQuantity += material.total_quantity;
          siteTotalQuantityInKg += material.total_quantity_by_kg;
          siteTotalAmount += material.total_amount;
        });

        cityTotalQuantity += siteTotalQuantity;
        cityTotalQuantityInKg += siteTotalQuantityInKg;
        cityTotalAmount += siteTotalAmount;

        tableData.push({
          key: `${cityIndex}-${siteIndex}`,
          srNo: siteIndex === 0 && cityIndex + 1,
                
          cityName: siteIndex === 0 ? city.city_name : "",
          cityRowSpan: siteIndex === 0 ? cityRowSpan : 0,
          siteName: site.site_name,
          siteData: materials,
          siteLocations: materials.map((material, ind) => {
            const diameter_wise = Object.values(material.diameter_wise_map || {});
            const showSubCategory = !seenSubCategories.has(material.material_type_sub_category);
            seenSubCategories.add(material.material_type_sub_category);
            return {
              vendors: material.vendors,
              cityName: city.city_name,
              siteId: site.site_name,
              matInd: ind,
              materialName: material.material_type_name,
              quantity: material.total_quantity,
              quantity_by_kg: material.total_quantity_by_kg.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              }),
              remark: material.remark,
              uom: material.preferred_unit_of_measurement,
              uom_pref: material.preferred_unit_of_measurement_id,
              subCategory: showSubCategory ? material.material_type_sub_category : "",
              estimatedPrice: material.estimated_material_unit_price,
              materialTotal: material.total_amount,
              unit_measurement: material.material_type_uom,
              unit_measurement_name: material.material_type_uom_name,
              purchase_requisition_material_id: material.purchase_requisition_material_id,
              rfq_material_idd: material.rfq_material_id,
              diameter_wise,
            };
          }),
          totalAmount: (
            <Input
              disabled
              value={`\u20B9 ${siteTotalAmount.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}`}
              className="site-subtotal"
            />
          ),
          siteTotalQuantity: (
            <Input
              disabled
              value={siteTotalQuantity.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
              className="site-total-quantity"
              readOnly
            />
          ),
        });

        tableData.push({
          key: `${cityIndex}-${siteIndex}-SiteSubTotal`,
          siteName: "Site Sub-Total",
          siteLocations: [
            {
              totalQuantity: siteTotalAmount,
              materialName: "",
              quantity: "",
              quantity_by_kg: "",
              subCategory: "",
              cityId: city.city_id,
              siteId: site.site_id,
            },
          ],
          totalAmount: (
            <Input
              disabled
              value={`\u20B9 ${siteTotalAmount.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}`}
              className="site-subtotal"
            />
          ),
          siteTotalQuantity: (
            <Input
              disabled
              value={siteTotalQuantity.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
              className="site-total-quantity"
              readOnly
            />
          ),
        });

        if (cityWisePr.length > 1 ) {
          tableData.push({
            key: `${cityIndex}-${siteIndex}-CityTotal`,
            siteName: "City Total",
            siteLocations: [
              {
                materialName: "",
                cityTotal: cityTotalAmount,
                quantity: "",
                quantity_by_kg: "",
                subCategory: "",
                cityId: city.city_id,
                siteId: site.site_id,
              },
            ],
            totalAmount: (
              <Input
                disabled
                value={`\u20B9 ${cityTotalAmount.toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}`}
                className="city-total"
              />
            ),
            cityTotalQuantity: (
              <Input
                disabled
                value={cityTotalQuantity.toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}
                className="city-total-quantity"
                readOnly
              />
            ),
          });
        }
      });

      city.city_total_quantity = cityTotalQuantity;
    });

    setTableData(tableData);
  };

  const getVendorIds = (siteIndex) => {
    const vendors = siteIndex?.vendors || [];
    const hasSelectedVendor = vendors.some((v) => v.is_selected_vendor);
    return hasSelectedVendor
      ? vendors.filter((v) => v.is_selected_vendor).map((v) => v.id)
      : vendors.map((v) => v.id);
  };

  const createPayload = (tableData) => {
    const payload = {
      rfq_master: masterId,
      rfq_site_details: [],
    };

    const siteGroupedData = {};

    tableData.forEach((site) => {
      if (!site.siteLocations || site.siteName === "Site Sub-Total") return;

      site.siteData.forEach((location) => {
        const siteIndex = site.siteLocations.find(
          (siteLocation) =>
            siteLocation.siteId === site.siteName &&
            siteLocation.materialName === location.material_type_name
        );

        if (siteIndex) {
          const siteName = site.siteName;

          if (!siteGroupedData[siteName]) {
            siteGroupedData[siteName] = {
              purchase_requisition_site_name: siteName,
              rfq_material_details: [],
            };
          }

          siteGroupedData[siteName].rfq_material_details.push({
            id: siteIndex.rfq_material_idd || null,
            material_total_quantity: location.total_quantity,
            material_total_quantity_by_kg: location.total_quantity_by_kg,
            amount: location.total_amount,
            purchase_requisition_material: location.purchase_requisition_material_id,
            unit_of_measurement: location.unit_measurement,
            vendor: getVendorIds(siteIndex),
          });
        }
      });
    });

    payload.rfq_site_details = Object.values(siteGroupedData);
    return payload;
  };

  const createPayload2 = (tableData) => {
    const payload = {
      rfq_master: masterId,
      rfq_site_details: [],
    };

    const siteGroupedData = {};

    tableData.forEach((site) => {
      if (!site.siteLocations || site.siteName === "Site Sub-Total") return;

      site.siteData.forEach((location) => {
        const siteIndex = site.siteLocations.find(
          (siteLocation) =>
            siteLocation.siteId === site.siteName &&
            siteLocation.materialName === location.material_type_name
        );

        if (siteIndex) {
          const siteName = site.siteName;

          if (!siteGroupedData[siteName]) {
            siteGroupedData[siteName] = {
              purchase_requisition_site_name: siteName,
              rfq_material_details: [],
            };
          }

          siteGroupedData[siteName].rfq_material_details.push({
            id: siteIndex.rfq_material_idd || null,
            material_total_quantity: location.total_quantity,
            material_total_quantity_by_kg: location.total_quantity_by_kg,
            amount: location.total_amount,
            purchase_requisition_material: location.purchase_requisition_material_id,
            unit_of_measurement: location.unit_measurement,
          });
        }
      });
    });

    payload.rfq_site_details = Object.values(siteGroupedData);
    return payload;
  };

  const handleSubmit = async () => {
    try {
      setBtnLoading(true);
      const hasEmptyVendor = tableData.some((item) =>
        item.siteData?.some(
          (material) => !material.vendors || material.vendors.length === 0
        )
      );

      if (hasEmptyVendor) {
        ErrorNotificationMsg("Select At Least One Vendor");
        return;
      }

      const payload = createPayload(tableData);
      const payload2 = createPayload2(tableData);

      let apiResponse;
      if (rfqMaterialDetailsID) {
        apiResponse = await patchRequest(`rfq-material-details/${masterId}/`, payload);
      } else {
        apiResponse = await postRequest(`rfq-material-details/`, payload2);
      }

      if (apiResponse.data.success) {
        SuccessNotificationMsg(apiResponse.data.message);
        navigate(`/request-for-quotation/history`);
      } else {
        ErrorNotificationMsg(apiResponse.data.message);
      }
    } catch (error) {
      console.error("Error submitting RFQ:", error);
      ErrorNotificationMsg("Failed to submit RFQ");
    } finally {
      setBtnLoading(false);
    }
  };

  const diameterColumns = diameterList.map((dia) => ({
    title: dia,
    dataIndex: "siteLocations",
    width: 80,
    // align: "center",
    render: (siteLocations, record) => {
      if (record.siteName === "Site Sub-Total" || record.siteName === "City Total") {
        return null;
      }
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
          {siteLocations.map((site, index) => {
            const found = site.diameter_wise?.find((d) => `${d.diameter_name.toUpperCase()}` === dia);
            return (
              <div key={index} className="lblvalue">
                {found ? found.quantity : "-"}
              </div>
            );
          })}
        </div>
      );
    },
  }));

  const columns = [
    {
      title: "Sr.",
      dataIndex: "srNo",
      width: 30,
    },
    {
      title: "Site",
      dataIndex: "siteName",
      width: 100,
      render: (siteName, record) => {
        if (record.siteName === "Site Sub-Total") {
          return <div className="subtotal">Sub Total</div>;
        } else if (record.siteName === "City Total") {
          return <div className="citytotal">{siteName}</div>;
        }
        return <div>{siteName}</div>;
      },
    },
    {
      title: "Sub category",
      dataIndex: "siteLocations",
      width: 100,
      render: (siteLocations) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
          {siteLocations.map((site, index) => (
            <div key={index} className="lblvalue">
              {site.subCategory || ""}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Grade & Quality",
      dataIndex: "siteLocations",
      width: 220,
      render: (siteLocations) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "22px", width: "220px" }}>
          {siteLocations.map((site, index) => (
            <div key={index} className="lblvalue">
              {site.materialName}
            </div>
          ))}
        </div>
      ),
    },
    ...diameterColumns,
    {
      title: (
        <Tooltip
          color="#094E8F"
          placement="top"
          title="Preferred Unit of measurement means Your company buys material generally in this unit of measurement"
        >
          UoM.
        </Tooltip>
      ),
      width: 120,
      dataIndex: "siteLocations",
      render: (siteLocations) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
          {siteLocations.map((site, index) => (
            <div key={index} className="lblvalue">
              {site.unit_measurement_name || ""}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Qty. In Kg",
      dataIndex: "siteLocations",
      width: 100,
      align: "right",
      render: (siteLocations) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
          {siteLocations.map((site, index) => (
            <div
              key={index}
              className="lblvalue"
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              {site.quantity_by_kg || ""}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Est. Amount",
      dataIndex: "siteLocations",
      align: "right",
      width: "130px",
      render: (siteLocations, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "22px", alignItems: "flex-end" }}>
          {siteLocations.map((site, index) => {
            if (record.siteName === "Site Sub-Total") {
              return (
                <div
                  key={index}
                  className="lblvalue subtotal"
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  ₹ {site.totalQuantity.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </div>
              );
            } else if (record.siteName === "City Total") {
              return (
                <div
                  key={index}
                  className="lblvalue citytotal"
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  ₹ {site.cityTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </div>
              );
            }
            return (
              <div
                key={index}
                className="lblvalue"
                style={{ display: "flex", justifyContent: "flex-end" }}
              >
                ₹ {site.materialTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </div>
            );
          })}
        </div>
      ),
    }
  ];

  if (cityLength > 1) {
    columns.splice(1, 0, {
      title: "City",
      dataIndex: "cityName",
      key: "cityName",
      width: 100,
      render: (text, record, index) => {
        if (index === 0 || record.cityName !== tableData[index - 1]?.cityName) {
          return <div className="lblvalue">{text}</div>;
        }
        return <div className="lblvalue" />;
      },
    });
  }

  return (
    <section className="grid-sec mt-5">
      <div className="PR-body tbl-collapse" style={{ padding: "0" }}>
        <Spin spinning={apiLoading}>
          {response?.city_wise_pr && (
                <Table
                  columns={columns}
                  dataSource={tableData}
                  pagination={false}
                  rowKey="key"
                />
           
          )}
        </Spin>
      </div>
    </section>
  );
};

ViewRFQDiameterWise.propTypes = {
  getResponseData: PropTypes.object
};

export default ViewRFQDiameterWise;

